import { useCallback, useEffect, useState } from "react";
import {
  getAllUsers,
  createNewUser,
  updateUser,
} from "../services/users/user.api";
import { toast } from "../components/Toast/Toast";
import DataTable from "../components/Table/DataTable";
import {
  Tooltip,
  IconButton,
  TextField,
  Button,
  Modal,
  Chip,
} from "@mui/material";
import { Formik } from "formik";
import EditIcon from "@mui/icons-material/Edit";
import * as yup from "yup";
import { MdClose } from "react-icons/md";
import Card from "@mui/material/Card";

export default function User() {
  const [users, setUsers] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedUser, setSelectedUser] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [openUserModal, setOpenUserModal] = useState(false);

  const columns = [
    {
      field: "actions",
      headerName: "Actions",
      width: 50,
      renderCell: (row) => (
        <Tooltip title="Edit">
          <IconButton
            size="small"
            onClick={() => {
              setSelectedUser(row);
              setOpenUserModal(true);
            }}
            className="text-primary hover:bg-primary/10"
          >
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
    {
      field: "username",
      headerName: "User Name",
      width: 400,
    },
    {
      field: "email",
      headerName: "Email",
      width: 400,
    },
    {
      field: "role",
      headerName: "Role",
      renderCell: (row) => {
        return row.role === 0 ? (
          <Chip label="user" color="primary" variant="outlined" size="small" />
        ) : (
          <Chip label="admin" color="primary" variant="outlined" size="small" />
        );
      },
    },
  ];

  const fetchUsers = useCallback(() => {
    setLoading(true);
    getAllUsers({
      page: page + 1,
      limit: rowsPerPage,
      ...(searchText ? { search: searchText } : {}),
    })
      .then((res) => {
        setUsers(res.data);
        setTotalCount(res.totalResults);
      })
      .catch((err) => {
        toast.error("Failed to fetch users");
      })
      .finally(() => {
        setLoading(false);
      });
  }, [page, rowsPerPage, searchText]);

  useEffect(() => {
    let delay = searchText ? 500 : 0;

    const searchTimer = setTimeout(() => {
      fetchUsers();
    }, delay);

    return () => clearTimeout(searchTimer);
  }, [searchText, rowsPerPage, fetchUsers]);

  const handleOpenModal = () => {
    setSelectedUser(null);
    setOpenUserModal(true);
  };

  const handleCloseModal = () => {
    setOpenUserModal(false);
    setSelectedUser(null);
  };

  const handleSubmit = async (user) => {
    const payload = { ...user };

    if (selectedUser?._id) {
      // If updating and password is empty, remove it from payload
      if (!payload.password) {
        delete payload.password;
      }

      await updateUser(selectedUser._id, payload)
        .then((res) => {
          setUsers(users.map((u) => (u._id === res._id ? res : u)));
          handleCloseModal();
          toast.success("User updated successfully!");
        })
        .catch((err) => {
          console.error("Error updating user:", err);
          toast.error(err?.response?.data?.error || "Failed to update User");
        });
    } else {
      await createNewUser(user)
        .then((res) => {
          setUsers((prev) => {
            const updated = [res, ...prev];
            if (updated.length > rowsPerPage) {
              updated.pop();
            }
            return updated;
          });
          setTotalCount((prev) => prev + 1);
          handleCloseModal();
          toast.success("User created successfully!");
        })
        .catch((err) => {
          console.error("Error creating user:", err);
          toast.error(err?.response?.data?.error || "Failed to create User");
        });
    }
  };

  const handleEdit = (user) => {
    setSelectedUser(user);
    setOpenUserModal(true);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <div className="p-4">
      <div className="flex flex-row justify-between gap-2 w-full mb-4">
        <TextField
          label="Search by name"
          value={searchText}
          size="small"
          onChange={(e) => {
            setSearchText(e.target.value);
            setPage(0);
          }}
          variant="outlined"
          sx={{ minWidth: "200px" }}
          InputLabelProps={{ shrink: true }}
        />
        <Button
          variant="contained"
          sx={{ textTransform: "none", fontWeight: "bold" }}
          onClick={handleOpenModal}
        >
          Add
        </Button>
      </div>
      <DataTable
        rows={users}
        columns={columns}
        loading={loading}
        totalCount={totalCount}
        page={page}
        rowsPerPage={rowsPerPage}
        onEdit={handleEdit}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
      <Modal
        open={openUserModal}
        onClose={handleCloseModal}
        className="flex items-center justify-center p-4 backdrop-blur-sm bg-black/40"
        closeAfterTransition
      >
        <Card className="w-full max-w-2xl overflow-hidden rounded-xl shadow-2xl bg-white border-0 flex flex-col max-h-[90vh]">
          {/* Header */}
          <div className="px-6 py-4 flex justify-between items-center bg-gray-50/30 border-b border-gray-100">
            <h2 className="text-xl font-bold text-gray-800">
              {selectedUser ? "Edit User" : "Add New User"}
            </h2>
            <IconButton
              onClick={handleCloseModal}
              className="hover:bg-red-50 hover:text-red-500 transition-colors p-1"
            >
              <MdClose />
            </IconButton>
          </div>

          <Formik
            initialValues={{
              username: selectedUser?.username || "",
              email: selectedUser?.email || "",
              role: selectedUser?.role || 0,
              password: "",
            }}
            enableReinitialize
            validationSchema={yup.object({
              username: yup.string().required("User name is required"),
              email: yup
                .string()
                .email("Invalid email")
                .required("Email is required"),
              password: selectedUser
                ? yup
                    .string()
                    .test(
                      "len",
                      "Password should be at least 6 characters",
                      (val) => !val || val.length >= 6
                    )
                : yup
                    .string()
                    .min(6, "Password should be at least 6 characters")
                    .required("Password is required"),
              role: yup.mixed().required("Role is required"),
            })}
            onSubmit={handleSubmit}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleSubmit: formikSubmit,
              isSubmitting,
            }) => (
              <form
                onSubmit={formikSubmit}
                className="flex flex-col flex-1 overflow-hidden"
              >
                <div className="p-8 space-y-8 overflow-y-auto flex-1">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                    <TextField
                      fullWidth
                      label="User Name"
                      name="username"
                      value={values.username}
                      size="small"
                      onChange={handleChange}
                      error={touched.username && Boolean(errors.username)}
                      helperText={touched.username && errors.username}
                      autoComplete="off"
                      variant="outlined"
                      InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                      fullWidth
                      label="Email"
                      name="email"
                      value={values.email}
                      size="small"
                      onChange={handleChange}
                      error={touched.email && Boolean(errors.email)}
                      helperText={touched.email && errors.email}
                      disabled={Boolean(selectedUser)}
                      variant="outlined"
                      InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                      fullWidth
                      label="Password"
                      name="password"
                      type="password"
                      value={values.password}
                      size="small"
                      onChange={handleChange}
                      error={touched.password && Boolean(errors.password)}
                      helperText={
                        touched.password && errors.password
                          ? errors.password
                          : selectedUser
                          ? "Leave blank for same password"
                          : null
                      }
                      variant="outlined"
                      InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                      fullWidth
                      label="Role"
                      name="role"
                      value={values.role}
                      size="small"
                      disabled
                      variant="outlined"
                      InputLabelProps={{ shrink: true }}
                    />
                  </div>
                </div>

                {/* Footer Actions */}
                <div className="flex justify-end gap-3 p-6 border-t border-gray-100 bg-gray-50/30">
                  <Button
                    onClick={handleCloseModal}
                    sx={{
                      textTransform: "none",
                      fontWeight: "bold",
                      fontSize: "14px",
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={isSubmitting}
                    sx={{
                      textTransform: "none",
                      fontWeight: "bold",
                      fontSize: "14px",
                    }}
                  >
                    {selectedUser ? "Update" : "Create"}
                  </Button>
                </div>
              </form>
            )}
          </Formik>
        </Card>
      </Modal>
    </div>
  );
}
